import { eventLayoutCopyIssue, experienceTargetOptions, PHASES, resolveEditingPresentation, templateMatches } from './experienceModel'

// A game page is one event template: All games (the default), or the pages
// for one sport, one competition or one game, each starting from the broader
// ones. The editor works in two states, Before start and Live. Before start
// edits the page itself, so live games share it; Live edits only live games.

const SPECIFICITY = ['default', 'sport', 'league', 'event']

export const GAME_STATES = PHASES

export function gamePageLabel(template) {
  return template.scope.kind === 'default' ? 'All games' : template.name
}

export function sortedGamePages(experience) {
  return [...experience.event_templates].sort((a, b) => SPECIFICITY.indexOf(a.scope.kind) - SPECIFICITY.indexOf(b.scope.kind) || gamePageLabel(a).localeCompare(gamePageLabel(b)))
}

export function selectedGamePage(experience, selection) {
  return experience.event_templates.find((item) => item.id === selection.templateId) ?? experience.event_templates.find((item) => item.scope.kind === 'default')
}

export function gamePageState(selection) {
  return selection.phase === 'in_play' ? 'in_play' : 'pre_match'
}

/** Games this page applies to, live ones first while editing Live. */
export function exampleGames(template, catalog, state) {
  const games = catalog.events.filter((event) => templateMatches(template, event))
  return state === 'in_play' ? games.toSorted((a, b) => Number(Boolean(b.live)) - Number(Boolean(a.live))) : games
}

/** The page's broader pages, most general first, without the page itself. */
export function broaderGamePages(experience, template) {
  const level = SPECIFICITY.indexOf(template.scope.kind)
  return sortedGamePages(experience).filter((item) => item.id !== template.id && SPECIFICITY.indexOf(item.scope.kind) < level)
}

/** What customers see for the example game in this state, and where each value comes from. */
export function resolveGamePage(experience, template, event, state) {
  return resolveEditingPresentation(experience, template, event, state)
}

/** Fields this page sets in this state; the rest come from a broader page or Before start. */
export function gamePageOverrides(template, state) {
  if (state === 'in_play') return new Set(Object.keys(template.scenes?.in_play?.layout ?? {}))
  return new Set([...Object.keys(template.layout ?? {}), ...Object.keys(template.scenes?.pre_match?.layout ?? {})])
}

/**
 * Sets (or with undefined, resets) one layout field on a page. Before start
 * writes the page's own layout and clears an older Before start-only value
 * that would hide the edit; Live writes the live override.
 */
export function setGamePageField(experience, templateId, state, field, value, uuid = () => crypto.randomUUID()) {
  const next = structuredClone(experience)
  const template = next.event_templates.find((item) => item.id === templateId)
  if (!template) return next
  if (state === 'in_play') {
    if (value === undefined && !template.scenes?.in_play) return next
    template.scenes ||= {}
    template.scenes.in_play ||= { id: uuid(), layout: {} }
    if (value === undefined) delete template.scenes.in_play.layout[field]
    else template.scenes.in_play.layout[field] = value
    return next
  }
  if (value === undefined) delete template.layout[field]
  else template.layout[field] = value
  if (template.scenes?.pre_match?.layout) delete template.scenes.pre_match.layout[field]
  return next
}

export function gamePageScopeIssue(experience, scope) {
  return eventLayoutCopyIssue(experience, scope)
}

export function addGamePage(experience, scope, catalog, uuid = () => crypto.randomUUID()) {
  const issue = gamePageScopeIssue(experience, scope)
  if (issue) throw new Error(issue)
  const value = scope.value.trim()
  const name = experienceTargetOptions(catalog, scope.kind).find((option) => option.value === value)?.label ?? value
  const template = { id: uuid(), screen_id: uuid(), name: name.slice(0, 80), scope: { kind: scope.kind, value }, layout: {} }
  return { experience: { ...experience, event_templates: [...experience.event_templates, template] }, template }
}

const comparable = (field, value) => JSON.stringify(field === 'sections' ? value?.map((item) => [item.type, item.enabled]) : value)

/**
 * Fields this page sets in this state that differ from what it would inherit
 * without them, so a reset is offered only where it changes the page.
 */
export function gamePageChanges(experience, template, event, state) {
  const presentation = resolveGamePage(experience, template, event, state)
  return new Set([...gamePageOverrides(template, state)].filter((field) => {
    const inherited = resolveGamePage(setGamePageField(experience, template.id, state, field, undefined, () => 'reset'), template, event, state)
    return comparable(field, inherited[field]) !== comparable(field, presentation[field])
  }))
}
